/**
 * Lumia — <lh-predictive-search> (vanilla ES2019).
 *
 * Wraps a search <form> (input[name="q"] type="search") plus a results
 * container [data-search-results]. On input (≥ 2 chars, debounced 250 ms) it
 * fetches
 *   {routes.predictive_search_url}?q=…&resources[type]=product,collection,page,article
 *     &resources[limit]=6&section_id=predictive-search
 * and injects the HTML rendered by sections/predictive-search.liquid.
 *
 * Attributes:
 *   data-search-url    predictive search endpoint (defaults to Lumia.routes)
 *   data-limit         results per resource type (default 6)
 *   data-section-id    section to render (default "predictive-search")
 *   data-disabled      present → element does nothing (plain form submit)
 *
 * Optional children:
 *   [data-search-status]  live region for result summaries / errors
 *   [data-search-idle]    shown while there is no query (e.g. suggestions)
 *   [data-search-reset]   clear button (hidden while the input is empty)
 *
 * Keyboard: ArrowDown/Up move through [data-search-item] links, Home/End jump,
 * Escape closes the results first (second Escape reaches the drawer), Enter on
 * the input submits the form (full search page).
 */
(function () {
  'use strict';

  if (customElements.get('lh-predictive-search')) return;

  var DEBOUNCE_MS = 250;
  var MIN_CHARS = 2;
  var MAX_CACHE = 30;
  var cache = new Map();

  function cacheSet(key, value) {
    if (cache.size >= MAX_CACHE) {
      var oldest = cache.keys().next().value;
      cache.delete(oldest);
    }
    cache.set(key, value);
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  var LhPredictiveSearch = /** @class */ (function () {
    function LhPredictiveSearch() {
      var self = Reflect.construct(HTMLElement, [], LhPredictiveSearch);
      self._controller = null;
      self._debounce = null;
      self._lastQuery = '';
      self._open = false;
      return self;
    }

    LhPredictiveSearch.prototype = Object.create(HTMLElement.prototype, { constructor: { value: LhPredictiveSearch } });

    LhPredictiveSearch.prototype.connectedCallback = function () {
      var self = this;
      if (this._ready) return;

      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"], input[name="q"]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      this.idle = this.querySelector('[data-search-idle]');
      this.reset = this.querySelector('[data-search-reset]');
      this.host = this.closest('.lh-drawer');

      if (!this.form || !this.input) return;

      this.form.addEventListener('submit', function (event) {
        if (!self.input.value.trim()) {
          event.preventDefault();
          self.input.focus();
        }
      });

      if (this.reset) {
        this.reset.addEventListener('click', function (event) {
          event.preventDefault();
          self.clear();
          self.input.focus();
        });
      }

      this.input.addEventListener('input', function () { self._onInput(); });
      this._updateReset();

      if (this.hasAttribute('data-disabled') || !this.results) {
        this._ready = true;
        return;
      }

      var routes = (window.Lumia && window.Lumia.routes) || {};
      this.url = this.getAttribute('data-search-url') || routes.predictive_search_url || '/search/suggest';
      this.limit = parseInt(this.getAttribute('data-limit'), 10) || 6;
      this.sectionId = this.getAttribute('data-section-id') || 'predictive-search';

      this.input.setAttribute('autocomplete', 'off');
      // No aria-expanded/aria-autocomplete: the input keeps its implicit searchbox
      // role (those attributes are not allowed on it); [data-search-status] announces results.
      if (this.results.id) this.input.setAttribute('aria-controls', this.results.id);

      this.input.addEventListener('focus', function () {
        if (!self._open && self.results.innerHTML && self.input.value.trim().length >= MIN_CHARS) {
          self._openResults();
        }
      });
      this.input.addEventListener('keydown', function (event) { self._onKeydown(event); });
      this.results.addEventListener('keydown', function (event) { self._onKeydown(event); });
      this.results.addEventListener('click', function (event) {
        var item = event.target.closest('[data-search-item]');
        if (item) self._setActive(item);
      });

      this._ready = true;

      // Skript se učitava lenjo (header.js): ako je korisnik već otkucao upit dok je fajl stizao,
      // pokreni pretragu odmah. Guard na activeElement sprečava fetch za zatvorene fioke na
      // /search?q=… gde input već ima vrednost.
      if (document.activeElement === this.input && this.input.value.trim().length >= MIN_CHARS) {
        this._onInput();
      }
    };

    LhPredictiveSearch.prototype.disconnectedCallback = function () {
      window.clearTimeout(this._debounce);
      if (this._controller) this._controller.abort();
    };

    /* --------------------------------------------------------------- input */
    LhPredictiveSearch.prototype._onInput = function () {
      var self = this;
      var query = this.input.value.trim();
      this._updateReset();
      window.clearTimeout(this._debounce);
      if (!this.results || this.hasAttribute('data-disabled')) return;

      if (query.length < MIN_CHARS) {
        this._closeResults(true);
        return;
      }
      this._debounce = window.setTimeout(function () { self.search(query); }, DEBOUNCE_MS);
    };

    LhPredictiveSearch.prototype._updateReset = function () {
      if (!this.reset) return;
      this.reset.hidden = !this.input.value.length;
    };

    LhPredictiveSearch.prototype.clear = function () {
      window.clearTimeout(this._debounce);
      if (this._controller) this._controller.abort();
      this.input.value = '';
      this._updateReset();
      this._setLoading(false);
      this._closeResults(true);
    };

    /* --------------------------------------------------------------- fetch */
    LhPredictiveSearch.prototype.search = function (query) {
      var self = this;
      if (query === this._lastQuery && this.results.innerHTML) {
        this._openResults();
        return;
      }
      this._lastQuery = query;

      if (cache.has(query)) {
        if (this._controller) { this._controller.abort(); this._controller = null; }
        this._setLoading(false);
        this._render(cache.get(query), query);
        return;
      }

      if (this._controller) this._controller.abort();
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      this._controller = controller;
      this._setLoading(true);

      var params = [
        'q=' + encodeURIComponent(query),
        'resources[type]=product,collection,page,article',
        'resources[limit]=' + this.limit,
        'resources[limit_scope]=each',
        'resources[options][unavailable_products]=last',
        'resources[options][fields]=title,product_type,variants.title,vendor,tag',
        'section_id=' + encodeURIComponent(this.sectionId)
      ].join('&');

      var separator = this.url.indexOf('?') === -1 ? '?' : '&';
      var options = { headers: { Accept: 'text/html' }, credentials: 'same-origin' };
      if (controller) options.signal = controller.signal;

      fetch(this.url + separator + params, options)
        .then(function (response) {
          if (!response.ok) throw new Error('Predictive search failed: ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var section = doc.getElementById('shopify-section-' + self.sectionId);
          var markup = section ? section.innerHTML : html;
          cacheSet(query, markup);
          if (self.input.value.trim() === query) self._render(markup, query);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          if (self.input.value.trim() === query) self._renderError();
        })
        .then(function () {
          if (self._controller === controller) {
            self._controller = null;
            self._setLoading(false);
          }
        });
    };

    /* -------------------------------------------------------------- render */
    LhPredictiveSearch.prototype._render = function (markup, query) {
      this.results.innerHTML = markup;
      this._openResults();
      var summary = this.results.querySelector('[data-search-summary]');
      this._announce(summary ? summary.textContent : '');
      this.dispatchEvent(new CustomEvent('predictive-search:results', {
        bubbles: true,
        detail: { query: query, count: this._count() }
      }));
    };

    LhPredictiveSearch.prototype._renderError = function () {
      var message = this.getAttribute('data-error-text') || 'Error';
      this.results.innerHTML = '<p class="lh-predictive__error" role="alert">' + message + '</p>';
      this._openResults();
      this._announce(message);
    };

    LhPredictiveSearch.prototype._count = function () {
      var wrap = this.results.querySelector('[data-predictive-results]');
      var count = wrap ? parseInt(wrap.getAttribute('data-count'), 10) : 0;
      return isNaN(count) ? 0 : count;
    };

    LhPredictiveSearch.prototype._openResults = function () {
      this._open = true;
      this.results.hidden = false;
      this.classList.add('has-results');
      if (this.idle) this.idle.hidden = true;
      if (this.host) this.host.classList.add('is-searching');
    };

    LhPredictiveSearch.prototype._closeResults = function (clear) {
      this._open = false;
      this.results.hidden = true;
      this.classList.remove('has-results');
      if (this.idle) this.idle.hidden = false;
      if (this.host) this.host.classList.remove('is-searching');
      if (clear) {
        this.results.innerHTML = '';
        this._lastQuery = '';
        this._announce('');
      }
    };

    LhPredictiveSearch.prototype._setLoading = function (on) {
      this.classList.toggle('is-loading', !!on);
      if (on) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
      if (on && this.status && this.getAttribute('data-loading-text')) {
        this._announce(this.getAttribute('data-loading-text'));
      }
    };

    LhPredictiveSearch.prototype._announce = function (text) {
      if (!this.status) return;
      this.status.textContent = text || '';
    };

    /* ------------------------------------------------------------ keyboard */
    LhPredictiveSearch.prototype._items = function () {
      return toArray(this.results.querySelectorAll('[data-search-item]'));
    };

    LhPredictiveSearch.prototype._setActive = function (item) {
      this._items().forEach(function (el) { el.classList.toggle('is-active', el === item); });
    };

    LhPredictiveSearch.prototype._onKeydown = function (event) {
      if (!this._open) {
        if (event.key === 'ArrowDown' && this.results.innerHTML && this.input.value.trim().length >= MIN_CHARS) {
          event.preventDefault();
          this._openResults();
          this._focusIndex(0);
        }
        return;
      }

      var items = this._items();
      var index = items.indexOf(document.activeElement);

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this._focusIndex(index + 1 >= items.length ? 0 : index + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (index <= 0) {
            this._setActive(null);
            this.input.focus();
          } else {
            this._focusIndex(index - 1);
          }
          break;
        case 'Home':
          if (index !== -1) { event.preventDefault(); this._focusIndex(0); }
          break;
        case 'End':
          if (index !== -1) { event.preventDefault(); this._focusIndex(items.length - 1); }
          break;
        case 'Escape':
          event.preventDefault();
          event.stopPropagation();
          this._closeResults(false);
          this.input.focus();
          break;
        case 'Tab':
          // Leaving the results with Tab keeps them open; nothing to do.
          break;
        default:
          break;
      }
    };

    LhPredictiveSearch.prototype._focusIndex = function (index) {
      var items = this._items();
      if (!items.length) return;
      var target = items[Math.max(0, Math.min(index, items.length - 1))];
      this._setActive(target);
      target.focus();
    };

    return LhPredictiveSearch;
  })();
  Object.setPrototypeOf(LhPredictiveSearch, HTMLElement);

  customElements.define('lh-predictive-search', LhPredictiveSearch);
})();
